/**
 * Easy Reports — config + compiler (Carlos report builder, 2026-07-13).
 *
 * SINGLE SOURCE OF TRUTH for the business-term → schema mapping:
 * subjects (business entity → table + date field), field bundles
 * (friendly labels → columns), templates (the §3 gallery), and date
 * presets. The UI never shows a raw table or column name; everything
 * here compiles down to a querySpec for the EXISTING reports:query /
 * reports:export:csv engine — so the #182 per-patient PHI audit is
 * inherited automatically.
 *
 * Grouping is CLIENT-SIDE presentation (the engine has no aggregate
 * SELECT): queries stay row-listings, which keeps full per-patient
 * audit instead of the GROUP-BY coarse-only carve-out.
 *
 * Pure module — no React, no platform imports — so the compiler is
 * unit-testable.
 */
// ─── Renders (how the UI displays a column) ─────────────────────────────────
// 'user' → resolve id via the users list; 'org' → resolve via the partner-org
// lookup; 'money_cents' → $x,xxx.xx; 'date' → locale date.

// ─── Subjects (business entities) ────────────────────────────────────────────
export const SUBJECTS = {
  people: {
    label: "People (leads & patients)",
    table: "crm_contacts",
    dateField: "created_at",
    dateLabel: "Created",
    defaultBundles: ["contact_info"],
    // #72 slice 1: a person row opens the CRM contact file. idField 'id' is
    // added to the query (not shown); the name cells are the clickable anchors.
    record: { type: "contact", idField: "id", linkFields: ["first_name", "last_name"] },
  },
  appointments: {
    label: "Appointments",
    table: "crm_appointments",
    dateField: "start_time",
    dateLabel: "Scheduled",
    defaultBundles: ["appointment_basics"],
  },
  therapies: {
    label: "Therapies",
    table: "crm_appointments",
    dateField: "start_time",
    dateLabel: "Scheduled",
    baseFilters: [{ column: "appointment_type", operator: "=", value: "procedure" }],
    defaultBundles: ["appointment_basics"],
  },
  commissions: {
    label: "Commissions",
    table: "commission_ledger",
    dateField: "earned_at",
    dateLabel: "Earned",
    defaultBundles: ["commission_basics"],
    // The "Partner" column (organization_id) is already selected; it is the
    // clickable anchor → partner record.
    record: { type: "partner", idField: "organization_id", linkFields: ["organization_id"] },
  },
  partners: {
    label: "Partners",
    table: "alliance_partner_organizations",
    dateField: "created_at",
    dateLabel: "Since",
    defaultBundles: ["partner_basics"],
    record: { type: "partner", idField: "id", linkFields: ["name"] },
  },
};

// ─── Field bundles (friendly labels → columns), per table ────────────────────
// NOTE: crm_opportunities.tags / ghl_custom_fields are encrypted envelopes
// (AES-256-GCM at rest) and are deliberately absent from every bundle.
export const BUNDLES = {
  crm_contacts: [
    { key: "contact_info", label: "Contact info", fields: [
      { column: "first_name", label: "First name" },
      { column: "last_name", label: "Last name" },
      { column: "phone", label: "Phone" },
      { column: "email", label: "Email" },
      { column: "referral_source", label: "Source" },
      { column: "assigned_to", label: "Owner", render: "user" },
    ]},
    { key: "clinical_summary", label: "Clinical summary", fields: [
      { column: "pipeline_stage", label: "Journey stage" },
      { column: "next_follow_up_date", label: "Next follow-up", render: "date" },
    ]},
    { key: "activity", label: "Activity", fields: [
      { column: "created_at", label: "Created", render: "date" },
      { column: "updated_at", label: "Last activity", render: "date" },
      { column: "last_contacted_at", label: "Last contact", render: "date" },
    ]},
  ],
  crm_appointments: [
    { key: "appointment_basics", label: "Appointment details", fields: [
      { column: "title", label: "Appointment" },
      { column: "appointment_type", label: "Type" },
      { column: "start_time", label: "When", render: "date" },
      { column: "location", label: "Clinic / location" },
      { column: "status", label: "Status" },
      { column: "assigned_to", label: "Doctor / owner", render: "user" },
    ]},
  ],
  commission_ledger: [
    { key: "commission_basics", label: "Commission details", fields: [
      { column: "organization_id", label: "Partner", render: "org" },
      { column: "row_type", label: "Entry type" },
      { column: "amount_cents", label: "Amount", render: "money_cents" },
      { column: "tier_at_earn", label: "Tier" },
      { column: "earned_at", label: "Earned", render: "date" },
    ]},
  ],
  alliance_partner_organizations: [
    { key: "partner_basics", label: "Partner details", fields: [
      { column: "name", label: "Partner" },
      { column: "current_tier", label: "Tier" },
      { column: "contact_email", label: "Contact email" },
      { column: "created_at", label: "Partner since", render: "date" },
    ]},
  ],
  crm_opportunities: [
    { key: "deal_basics", label: "Deal details", fields: [
      { column: "title", label: "Deal" },
      { column: "stage", label: "Stage" },
      { column: "status", label: "Status" },
      { column: "value", label: "Value", render: "money" },
      { column: "owner_id", label: "Owner", render: "user" },
      { column: "updated_at", label: "Last activity (ZGX)", render: "date" },
    ]},
  ],
};

// ─── Date presets (Daniel's ruling: BOTH sets + custom) ──────────────────────
export const DATE_PRESETS = [
  { key: "this_week", label: "This week", kind: "calendar" },
  { key: "this_month", label: "This month", kind: "calendar" },
  { key: "last_month", label: "Last month", kind: "calendar" },
  { key: "this_quarter", label: "This quarter", kind: "calendar" },
  { key: "last_quarter", label: "Last quarter", kind: "calendar" },
  { key: "this_year", label: "This year", kind: "calendar" },
  { key: "today", label: "Today", kind: "rolling" },
  { key: "yesterday", label: "Yesterday", kind: "rolling" },
  { key: "last_7", label: "Last 7 days", kind: "rolling" },
  { key: "last_14", label: "Last 14 days", kind: "rolling" },
  { key: "last_30", label: "Last 30 days", kind: "rolling" },
  { key: "last_90", label: "Last 90 days", kind: "rolling" },
  { key: "next_7", label: "Next 7 days", kind: "future" },
];

// Resolve a preset key to { from, to } (Date objects; to is exclusive; either
// may be null = unbounded on that side). RELATIVE by design: a saved preset
// re-evaluates at run time ("this week" always means the current week).
/*
 * ── BUSINESS TIME ───────────────────────────────────────────────────────────
 *
 * ⛔⛔ MEASURED, 30 Oct 2026: these presets were UTC-ANCHORED IN PRODUCTION.
 * The container sets no TZ, so Node defaults to UTC and `new Date(y, m, d)`
 * built a UTC midnight. "This month" in September therefore began at
 * 2026-09-01T00:00Z — which is 31 August, 8:00 PM in New York. Four hours of
 * August were counted as September, every month, on the CEO's dashboard and in
 * Easy Reports.
 *
 * ⚠⚠ THE PARITY TEST COULD NOT SEE IT. It compares this file with its ESM twin
 * inside ONE process, so both halves read the same `process.env.TZ` and agree
 * with each other while both are wrong. In production they do not even agree:
 * the server runs UTC and the browser runs the viewer's own zone.
 *
 * Every boundary below is now the America/New_York wall clock, explicitly.
 * Daniel's call, 30 Oct: one basis for the whole screen, accepting that every
 * existing figure shifts by up to four hours of data.
 */
const BUSINESS_TZ = "America/New_York";

/** The business-zone wall-clock parts of an instant. */
function tzParts(d) {
  const f = new Intl.DateTimeFormat("en-US", {
    timeZone: BUSINESS_TZ, year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false,
  }).formatToParts(d);
  const g = (t) => Number(f.find((p) => p.type === t).value);
  // ⚠ hour12:false still yields "24" for midnight in some ICU builds.
  return { y: g("year"), m: g("month"), d: g("day"), hh: g("hour") % 24, mi: g("minute"), ss: g("second") };
}

/**
 * The instant at which the business-zone wall clock reads midnight on y-m-d.
 *
 * ⚠ TWO PASSES, AND NO RED ARM ISOLATES THE SECOND — stated rather than
 * implied. Removing it leaves every test green, because the first guess
 * (midnight UTC) always lands around 19:00-20:00 the PREVIOUS business day,
 * nowhere near New York's 02:00 transition. The second pass is therefore
 * defensive against a BUSINESS_TZ whose offset changes AT midnight (Cuba and
 * Lord Howe have done exactly that), which this constant is not today. It is
 * kept because the constant is a constant, not a law — but it must not be read
 * as load-bearing for America/New_York, and a reader should not go looking for
 * the test that proves it.
 */
function tzMidnight(y, m, d) {
  let ts = Date.UTC(y, m - 1, d);
  for (let i = 0; i < 2; i += 1) {
    const p = tzParts(new Date(ts));
    const offset = Date.UTC(p.y, p.m - 1, p.d, p.hh, p.mi, p.ss) - ts;
    ts = Date.UTC(y, m - 1, d) - offset;
  }
  return new Date(ts);
}

/** Midnight, business time, on the day `now` falls in. */
function startOfBusinessDay(now) {
  const p = tzParts(now);
  return tzMidnight(p.y, p.m, p.d);
}

/** Shift a business-day start by whole days, re-anchored (DST-safe). */
function addBusinessDays(d, n) {
  const p = tzParts(d);
  return tzMidnight(p.y, p.m, p.d + n);
}

/**
 * Day of week (0=Sun) of a business-day start.
 *
 * ⚠ Read in UTC ON PURPOSE. `d` is already the instant of ET midnight, which is
 * 04:00 or 05:00 UTC on the SAME calendar date, so the UTC weekday is the
 * business weekday. Reading it with getDay() would re-introduce the process
 * timezone this whole block exists to remove.
 */
function businessWeekday(d) {
  return new Date(d).getUTCDay();
}

/** Midnight business time on the first of a month, offset by `n` months. */
function startOfBusinessMonth(now, n = 0) {
  const p = tzParts(now);
  return tzMidnight(p.y, p.m + n, 1);
}

export function presetRange(key, now = new Date()) {
  const today = startOfBusinessDay(now);
  const p = tzParts(now);
  switch (key) {
    case "this_week": { const iso = (businessWeekday(today) + 6) % 7; return { from: addBusinessDays(today, -iso), to: null }; }
    case "this_month": return { from: startOfBusinessMonth(now, 0), to: null };
    case "last_month": return { from: startOfBusinessMonth(now, -1), to: startOfBusinessMonth(now, 0) };
    case "this_quarter": { const q = Math.floor((p.m - 1) / 3) * 3; return { from: tzMidnight(p.y, q + 1, 1), to: null }; }
    case "last_quarter": { const q = Math.floor((p.m - 1) / 3) * 3; return { from: tzMidnight(p.y, q - 2, 1), to: tzMidnight(p.y, q + 1, 1) }; }
    case "this_year": return { from: tzMidnight(p.y, 1, 1), to: null };
    case "today": return { from: today, to: addBusinessDays(today, 1) };
    case "yesterday": return { from: addBusinessDays(today, -1), to: today };
    case "last_7": return { from: addBusinessDays(today, -7), to: null };
    case "last_14": return { from: addBusinessDays(today, -14), to: null };
    case "last_30": return { from: addBusinessDays(today, -30), to: null };
    case "last_90": return { from: addBusinessDays(today, -90), to: null };
    case "next_7": return { from: today, to: addBusinessDays(today, 8) };
    default: return { from: null, to: null };
  }
}

// ─── Templates (§3 gallery — seed set, expected to grow) ─────────────────────
/**
 * A caller-supplied From–To range, as a half-open business-time window.
 *
 * ⛔⛔ INCLUSIVE ON SCREEN, HALF-OPEN ON THE WIRE. Carlos picks "1 Sep – 15 Oct"
 * and means both days whole; every query in this repo compares
 * `col >= from AND col < to`. So `to` is midnight at the START OF THE DAY AFTER
 * the chosen To. Getting this wrong loses the last day silently — the figure is
 * still plausible, just smaller, which is the failure mode this dashboard has
 * shipped more than once.
 *
 * ⛔ REFUSES RATHER THAN REPAIRS. A coercer would turn "" into epoch and a
 * reversed pair into a negative window that `previousRange` then declines to
 * compare — a cascade of quiet wrongness from one bad keystroke. Each refusal
 * names what is wrong, because the message is the only thing the user sees.
 *
 * ⚠ MAX SPAN IS A PRODUCT DECISION, not a technical limit: 731 days (two years,
 * leap-safe). Long enough for any year-on-year question, short enough that a
 * mistyped year cannot ask for a decade of rows behind a PHI-bearing figure.
 */
const MAX_RANGE_DAYS = 731;

function parseYmd(s) {
  if (typeof s !== "string") return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s.trim());
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  if (mo < 1 || mo > 12 || d < 1 || d > 31) return null;
  // ⚠ Round-trip check: 2026-02-30 parses as three numbers and is not a date.
  const probe = tzMidnight(y, mo, d);
  const p = tzParts(probe);
  if (p.y !== y || p.m !== mo || p.d !== d) return null;
  return { y, m: mo, d, at: probe };
}

/**
 * @returns {{ok:true, from:Date, to:Date, label:string}|{ok:false, why:string}}
 */
function customRange(fromStr, toStr, now = new Date()) {
  const a = parseYmd(fromStr);
  const b = parseYmd(toStr);
  if (!a || !b) return { ok: false, why: "Enter both dates as YYYY-MM-DD." };
  if (a.at.getTime() > b.at.getTime()) {
    return { ok: false, why: "The From date must be on or before the To date." };
  }
  const todayStart = startOfBusinessDay(now);
  if (b.at.getTime() > todayStart.getTime()) {
    return { ok: false, why: "The To date cannot be in the future." };
  }
  // ⚠ Counted in DAYS, not milliseconds: a span crossing a DST change is 23 or
  // 25 hours long, and a millisecond count would make the limit wobble by one
  // day twice a year.
  const days = Math.round((b.at.getTime() - a.at.getTime()) / 86400000) + 1;
  if (days > MAX_RANGE_DAYS) {
    return { ok: false, why: `Choose a range of ${MAX_RANGE_DAYS} days or fewer (that one is ${days}).` };
  }
  return {
    ok: true,
    from: a.at,
    // ⭐ The day AFTER To — see the header. This is the whole of "inclusive".
    to: addBusinessDays(b.at, 1),
    label: formatRangeLabel(a, b),
  };
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun",
                "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** "1 Sep – 15 Oct 2026", dropping the year on the left when both share it. */
function formatRangeLabel(a, b) {
  const left = a.y === b.y
    ? `${a.d} ${MONTHS[a.m - 1]}`
    : `${a.d} ${MONTHS[a.m - 1]} ${a.y}`;
  return `${left} – ${b.d} ${MONTHS[b.m - 1]} ${b.y}`;
}

export { customRange, parseYmd, formatRangeLabel, MAX_RANGE_DAYS };

export const TEMPLATES = [
  {
    id: "new_leads_week", name: "New leads this week", icon: "UserPlus",
    blurb: "Every new contact, grouped by source",
    subject: "people", bundles: ["contact_info"],
    datePreset: "this_week",
    clientGroupBy: "referral_source",
  },
  {
    id: "therapies_by_doctor", name: "Therapies by doctor", icon: "Stethoscope",
    blurb: "Completed procedures, grouped by doctor",
    subject: "therapies", bundles: ["appointment_basics"],
    extraFilters: [{ column: "status", operator: "=", value: "completed" }],
    datePreset: "this_month",
    clientGroupBy: "assigned_to",
  },
  {
    id: "commission_by_partner", name: "Commission by partner", icon: "DollarSign",
    blurb: "Earned this month, per partner",
    subject: "commissions", bundles: ["commission_basics"],
    // All ledger row types included (therapy_commission / peptide_recurring /
    // parent_override / clawback_adjustment) — clawbacks net into the
    // per-partner subtotal, which is what "earned this month" means.
    datePreset: "this_month",
    clientGroupBy: "organization_id",
    groupSubtotal: "amount_cents",
  },
  {
    id: "stale_pipeline", name: "Stale pipeline", icon: "Hourglass",
    blurb: "Open deals with no activity in 14+ days",
    subject: "stale_deals", bundles: ["deal_basics"],
    // Staleness approximated by ZGX updated_at (the true GREATEST(updated_at,
    // last_ghl_activity_at) is computed and not filterable by this engine).
    staleDays: 14,
    extraFilters: [{ column: "status", operator: "=", value: "open" }],
    clientGroupBy: "stage",
  },
  {
    id: "upcoming_consults", name: "Upcoming consults", icon: "CalendarClock",
    blurb: "Next 7 days of consultations, by clinic",
    subject: "appointments", bundles: ["appointment_basics"],
    extraFilters: [
      { column: "appointment_type", operator: "=", value: "consultation" },
      { column: "status", operator: "!=", value: "cancelled" },
    ],
    datePreset: "next_7",
    clientGroupBy: "location",
  },
  {
    id: "partner_scorecard", name: "Partner scorecard", icon: "Award",
    blurb: "Partners with tier and status (v1 listing)",
    subject: "partners", bundles: ["partner_basics"],
    datePreset: "this_year",
    clientGroupBy: "current_tier",
  },
  {
    // P1b: aggregate cohort funnel. `report: "funnel"` routes the gallery to the
    // dedicated reports:funnel:run handler + FunnelReport display, NOT the
    // row-listing compile path (this template has no subject/bundles).
    id: "funnel_cohort", name: "Funnel (cohort)", icon: "Filter",
    blurb: "Where a cohort of leads reached — progression, drop-off, revenue",
    report: "funnel", datePreset: "this_month",
  },
  {
    // P1c: point-in-time distribution of the ACTIVE base across pipeline buckets
    // (current stage only). `report: "pipeline"` routes to reports:pipeline:
    // snapshot:run + the PipelineSnapshot display, not the row-listing path.
    id: "pipeline_snapshot", name: "Pipeline snapshot", icon: "Layers",
    blurb: "Where every active lead sits right now — counts, % of open, revenue pending",
    report: "pipeline", datePreset: null, // default all-time = true "right now"
  },
  {
    // P1d: per-rep performance (cohort funnel grouped by owner → milestone
    // columns). MANAGEMENT-ONLY: requiresPermission hides the gallery card unless
    // the viewer holds reports:management:read; the handler enforces it server-side.
    id: "rep_performance", name: "Sales rep performance", icon: "Award",
    blurb: "Per-rep leads → deposits → therapies, revenue, conversion (management)",
    report: "reps", datePreset: "this_month",
    requiresPermission: "reports:management:read",
  },
  {
    // §4.5 Phase 2: lead-source performance — the P1d cohort funnel grouped by
    // referral_source → milestone columns + booked revenue. Org-level marketing
    // aggregate (no per-person financials) → plain reports:read, so NO
    // requiresPermission (reports:read is the gallery's base gate).
    id: "lead_source", name: "Lead sources", icon: "Megaphone",
    blurb: "Leads → deposits → therapies + booked revenue, by referral source",
    report: "sources", datePreset: "this_month",
  },
  {
    // §4.4 Phase 2: funnel conversion — org-level cohort conversion analytics.
    // `report: "conversion"` routes to reports:conversion:run + the
    // ConversionReport display (four sections: stage-to-stage, time-in-stage +
    // velocity, period comparison, by-source). Org-level aggregate (no per-rep
    // breakout — that stays in the management-gated Rep Performance report) →
    // plain reports:read, so NO requiresPermission.
    id: "funnel_conversion", name: "Funnel conversion", icon: "TrendingUp",
    blurb: "Stage-to-stage conversion, time-in-stage & velocity, period comparison, by source",
    report: "conversion", datePreset: "this_month",
  },
];

// stale_deals is a template-only subject (not offered in build-your-own v1).
export const TEMPLATE_SUBJECTS = {
  ...SUBJECTS,
  stale_deals: {
    label: "Deals", table: "crm_opportunities",
    dateField: "updated_at", dateLabel: "Last activity",
    defaultBundles: ["deal_basics"],
  },
};

// ─── Compiler: easy config → reports:query querySpec ─────────────────────────
// cfg: { subject, bundles: [key], datePreset?, customFrom?, customTo?,
//        extraFilters?, staleDays?, clientGroupBy?, limit? }
// Returns { querySpec, display: { fields, clientGroupBy, groupSubtotal } }.
export function compileEasyReport(cfg, now = new Date()) {
  const subj = TEMPLATE_SUBJECTS[cfg.subject];
  if (!subj) throw new Error(`Unknown subject: ${cfg.subject}`);
  const table = subj.table;
  const allBundles = BUNDLES[table] || [];
  const chosen = allBundles.filter((b) => (cfg.bundles || []).includes(b.key));
  if (!chosen.length) throw new Error("Select at least one field group");

  // Columns: bundle fields + the client-group column + the date field (the
  // UI needs both to render groups and ranges), deduped, order-stable.
  const fields = [];
  const seen = new Set();
  for (const b of chosen) for (const f of b.fields) {
    if (!seen.has(f.column)) { seen.add(f.column); fields.push(f); }
  }
  const columns = fields.map((f) => f.column);
  // #72: ensure the openable record id is fetched (kept OUT of display.fields,
  // so it never renders as a column). id is reportable (not sensitive/denied).
  const rec = subj.record || null;
  for (const extra of [cfg.clientGroupBy, subj.dateField, rec && rec.idField]) {
    if (extra && !seen.has(extra)) { seen.add(extra); columns.push(extra); }
  }

  const filters = [...(subj.baseFilters || []), ...(cfg.extraFilters || [])];

  // Date range: custom beats preset; staleDays is the inverted window.
  if (cfg.staleDays && !cfg.customFrom && !cfg.customTo) {
    const cutoff = new Date(now.getTime() - cfg.staleDays * 24 * 60 * 60 * 1000);
    filters.push({ column: subj.dateField, operator: "<=", value: cutoff.toISOString() });
  } else {
    let from = null, to = null;
    if (cfg.customFrom || cfg.customTo) {
      from = cfg.customFrom ? new Date(cfg.customFrom) : null;
      to = cfg.customTo ? new Date(new Date(cfg.customTo).getTime() + 24 * 60 * 60 * 1000) : null; // inclusive end-day
    } else if (cfg.datePreset) {
      ({ from, to } = presetRange(cfg.datePreset, now));
    }
    if (from) filters.push({ column: subj.dateField, operator: ">=", value: from.toISOString() });
    if (to) filters.push({ column: subj.dateField, operator: "<", value: to.toISOString() });
  }

  return {
    querySpec: {
      table,
      columns,
      filters,
      orderBy: { column: subj.dateField, direction: "DESC" },
      limit: Math.min(Number(cfg.limit) || 2000, 10000),
    },
    display: {
      fields,
      clientGroupBy: cfg.clientGroupBy || null,
      groupSubtotal: cfg.groupSubtotal || null,
      dateField: subj.dateField,
      dateLabel: subj.dateLabel,
      // #72 slice 1: openable-record metadata for clickable names (null when
      // the subject has no openable record — appointments/therapies/deals).
      recordType: rec ? rec.type : null,
      idField: rec ? rec.idField : null,
      linkFields: rec ? rec.linkFields : [],
    },
  };
}

// Template → runnable easy config (date range overridable at run time).
export function templateToConfig(template) {
  return {
    kind: "easy_v1",
    templateId: template.id,
    subject: template.subject,
    bundles: [...template.bundles],
    extraFilters: template.extraFilters ? [...template.extraFilters] : [],
    datePreset: template.datePreset || null,
    staleDays: template.staleDays || null,
    clientGroupBy: template.clientGroupBy || null,
    groupSubtotal: template.groupSubtotal || null,
    customFrom: null,
    customTo: null,
  };
}


